import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, type InstitutionAdmin, type University } from "../api.js";

/**
 * Platform console: institutions and the administrators who run them.
 *
 * That is the whole of the super admin's power, and the page is built to say so.
 * Faculty, courses, assignments and student work belong to each institution and
 * appear nowhere here -- not hidden behind a permission check, simply absent.
 */
export default function SuperAdmin() {
  const [universities, setUniversities] = useState<University[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ name: "", code: "", allowedEmailDomain: "" });
  const [busy, setBusy] = useState(false);

  /** The institution whose detail panel is open, if any. */
  const [openId, setOpenId] = useState<string | null>(null);

  const load = () =>
    api
      .get<{ universities: University[] }>("/api/admin/universities")
      .then((r) => setUniversities(r.universities))
      .catch((e) => setError(e.message));

  useEffect(() => {
    void load();
  }, []);

  async function createUniversity(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const r = await api.post<{ university: University }>("/api/admin/universities", form);
      setForm({ name: "", code: "", allowedEmailDomain: "" });
      setShowNew(false);
      setStatus(
        `${r.university.name} created. It has no administrator yet — appoint one so it can add faculty.`
      );
      await load();
      // Open it straight away: a tenant without an admin is unusable, and the
      // next step is always the same one.
      setOpenId(r.university.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not create the institution.");
    } finally {
      setBusy(false);
    }
  }

  const totals = universities.reduce(
    (acc, u) => ({
      active: acc.active + (u.active ? 1 : 0),
      admins: acc.admins + u.adminCount,
      teachers: acc.teachers + u.teacherCount,
      students: acc.students + u.studentCount,
      courses: acc.courses + u.courseCount,
    }),
    { active: 0, admins: 0, teachers: 0, students: 0, courses: 0 }
  );
  const adminless = universities.filter((u) => u.active && u.adminCount === 0);

  return (
    <div className="page">
      <div className="between" style={{ marginBottom: 16 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22 }}>Institutions</h1>
          <div className="muted">Platform administration</div>
        </div>
        <div className="row">
          <Link to="/audit"><button>Audit log</button></Link>
          <button className="primary" onClick={() => setShowNew((v) => !v)}>
            {showNew ? "Cancel" : "New institution"}
          </button>
        </div>
      </div>

      {error && <div className="error-banner">{error}</div>}
      {status && <div className="notice">{status}</div>}

      <div className="card">
        <div className="stats">
          <div className="stat">
            <div className="value">{universities.length}</div>
            <div className="label">
              institutions{universities.length !== totals.active && ` · ${totals.active} active`}
            </div>
          </div>
          <div className="stat">
            <div className="value">{totals.admins}</div>
            <div className="label">administrators</div>
          </div>
          <div className="stat">
            <div className="value">{totals.teachers}</div>
            <div className="label">teachers</div>
          </div>
          <div className="stat">
            <div className="value">{totals.students}</div>
            <div className="label">students</div>
          </div>
          <div className="stat">
            <div className="value">{totals.courses}</div>
            <div className="label">courses</div>
          </div>
        </div>
      </div>

      {adminless.length > 0 && (
        <div className="notice">
          {adminless.map((u) => u.code).join(", ")}{" "}
          {adminless.length === 1 ? "has" : "have"} no administrator. Until one is appointed,
          nobody there can add faculty, courses or students.
        </div>
      )}

      <div className="notice">
        You administer institutions and the people who run them. Courses, assignments, exams and
        student submissions belong to each institution and are not visible from here.
      </div>

      {showNew && (
        <div className="card">
          <div style={{ marginBottom: 8, color: "var(--muted)", fontSize: 12 }}>NEW INSTITUTION</div>
          <form onSubmit={createUniversity}>
            <div className="grid-2">
              <label>
                <span>Name</span>
                <input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Demo Institute of Technology"
                  required
                  autoFocus
                />
              </label>
              <label>
                <span>Code (short, unique)</span>
                <input
                  value={form.code}
                  onChange={(e) => setForm({ ...form, code: e.target.value })}
                  placeholder="DEMO"
                  required
                />
              </label>
            </div>
            <label>
              <span>Restrict sign-in to email domain (optional)</span>
              <input
                value={form.allowedEmailDomain}
                onChange={(e) => setForm({ ...form, allowedEmailDomain: e.target.value })}
                placeholder="college.edu"
              />
            </label>
            <button className="primary" disabled={busy}>
              {busy ? "Creating…" : "Create institution"}
            </button>
          </form>
        </div>
      )}

      <div className="card">
        {universities.length === 0 ? (
          <div className="muted">
            No institutions yet. Create one, then appoint an administrator to run it.
          </div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Code</th>
                <th>Name</th>
                <th>Domain</th>
                <th>Admins</th>
                <th>Teachers</th>
                <th>Students</th>
                <th>Courses</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {universities.map((u) => (
                <Row
                  key={u.id}
                  university={u}
                  open={openId === u.id}
                  onToggle={() => setOpenId((id) => (id === u.id ? null : u.id))}
                  onChanged={load}
                  onStatus={setStatus}
                  onError={setError}
                />
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function Row({
  university: u,
  open,
  onToggle,
  onChanged,
  onStatus,
  onError,
}: {
  university: University;
  open: boolean;
  onToggle: () => void;
  onChanged: () => Promise<void> | void;
  onStatus: (s: string) => void;
  onError: (s: string) => void;
}) {
  return (
    <>
      <tr>
        <td><strong>{u.code}</strong></td>
        <td>{u.name}</td>
        <td className="muted">{u.allowedEmailDomain || "any"}</td>
        <td className={u.active && u.adminCount === 0 ? "" : "muted"}>
          {u.adminCount === 0 ? <span className="badge warn">none</span> : u.adminCount}
        </td>
        <td className="muted">{u.teacherCount}</td>
        <td className="muted">{u.studentCount}</td>
        <td className="muted">{u.courseCount}</td>
        <td>
          <span className={`badge ${u.active ? "pass" : "fail"}`}>
            {u.active ? "active" : "suspended"}
          </span>
        </td>
        <td>
          <button onClick={onToggle}>{open ? "Close" : "Manage"}</button>
        </td>
      </tr>
      {open && (
        <tr className="detail-row">
          <td colSpan={9}>
            <Detail
              university={u}
              onChanged={onChanged}
              onStatus={onStatus}
              onError={onError}
            />
          </td>
        </tr>
      )}
    </>
  );
}

/** Everything you can do to one institution, in one place. */
function Detail({
  university: u,
  onChanged,
  onStatus,
  onError,
}: {
  university: University;
  onChanged: () => Promise<void> | void;
  onStatus: (s: string) => void;
  onError: (s: string) => void;
}) {
  const [admins, setAdmins] = useState<InstitutionAdmin[] | null>(null);
  const [adminForm, setAdminForm] = useState({ name: "", email: "" });
  const [edit, setEdit] = useState({ name: u.name, allowedEmailDomain: u.allowedEmailDomain });
  const [busy, setBusy] = useState(false);

  const loadAdmins = () =>
    api
      .get<{ admins: InstitutionAdmin[] }>(`/api/admin/universities/${u.id}/admins`)
      .then((r) => setAdmins(r.admins))
      .catch((e) => onError(e.message));

  useEffect(() => {
    void loadAdmins();
  }, [u.id]);

  useEffect(() => {
    setEdit({ name: u.name, allowedEmailDomain: u.allowedEmailDomain });
  }, [u.name, u.allowedEmailDomain]);

  async function saveDetails(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      await api.patch(`/api/admin/universities/${u.id}`, edit);
      onStatus(`${u.code} updated.`);
      await onChanged();
    } catch (err) {
      onError(err instanceof Error ? err.message : "Could not update the institution.");
    } finally {
      setBusy(false);
    }
  }

  async function toggleActive() {
    const suspending = u.active;
    if (
      !confirm(
        suspending
          ? `Suspend ${u.name}?\n\nEveryone there is signed out on their next request and cannot sign back in. Nothing is deleted.`
          : `Reactivate ${u.name}?`
      )
    )
      return;
    try {
      await api.patch(`/api/admin/universities/${u.id}`, { active: !u.active });
      onStatus(`${u.name} ${suspending ? "suspended" : "reactivated"}.`);
      await onChanged();
    } catch (err) {
      onError(err instanceof Error ? err.message : "Could not update the institution.");
    }
  }

  async function addAdmin(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/api/admin/universities/${u.id}/admins`, adminForm);
      onStatus(`${adminForm.email} now administers ${u.name}.`);
      setAdminForm({ name: "", email: "" });
      await loadAdmins();
      await onChanged();
    } catch (err) {
      onError(err instanceof Error ? err.message : "Could not appoint the administrator.");
    } finally {
      setBusy(false);
    }
  }

  async function toggleAdmin(a: InstitutionAdmin) {
    const last = (admins ?? []).filter((x) => x.active).length === 1 && a.active;
    if (
      !confirm(
        a.active
          ? `Deactivate ${a.name}?\n\nThey lose access immediately.${
              last ? ` They are the only active administrator of ${u.code}, which will be left with none.` : ""
            }`
          : `Reactivate ${a.name}?`
      )
    )
      return;
    try {
      await api.patch(`/api/admin/users/${a.id}`, { active: !a.active });
      onStatus(`${a.email} ${a.active ? "deactivated" : "reactivated"}.`);
      await loadAdmins();
      await onChanged();
    } catch (err) {
      onError(err instanceof Error ? err.message : "Could not update the administrator.");
    }
  }

  return (
    <div className="detail-body">
      <section>
        <h3>ADMINISTRATORS</h3>
        {admins === null ? (
          <div className="muted">Loading…</div>
        ) : admins.length === 0 ? (
          <div className="muted" style={{ marginBottom: 12 }}>
            No administrator yet. Nobody at {u.code} can add faculty, courses or students until
            one is appointed.
          </div>
        ) : (
          <table style={{ marginBottom: 12 }}>
            <thead>
              <tr><th>Name</th><th>Email</th><th>Appointed</th><th>Status</th><th /></tr>
            </thead>
            <tbody>
              {admins.map((a) => (
                <tr key={a.id}>
                  <td>{a.name}</td>
                  <td className="muted">{a.email}</td>
                  <td className="muted">{new Date(a.createdAt).toLocaleDateString()}</td>
                  <td>
                    <span className={`badge ${a.active ? "pass" : "fail"}`}>
                      {a.active ? "active" : "inactive"}
                    </span>
                  </td>
                  <td>
                    <button className={a.active ? "danger" : ""} onClick={() => toggleAdmin(a)}>
                      {a.active ? "Deactivate" : "Reactivate"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        <form onSubmit={addAdmin}>
          <div className="grid-2">
            <label>
              <span>Name</span>
              <input
                value={adminForm.name}
                onChange={(e) => setAdminForm({ ...adminForm, name: e.target.value })}
                placeholder="Dr. Rao"
                required
              />
            </label>
            <label>
              <span>Email</span>
              <input
                type="email"
                value={adminForm.email}
                onChange={(e) => setAdminForm({ ...adminForm, email: e.target.value })}
                placeholder={`admin@${u.allowedEmailDomain || "college.edu"}`}
                required
              />
            </label>
          </div>
          <div className="row">
            <button className="primary" disabled={busy}>
              {busy ? "Appointing…" : "Appoint administrator"}
            </button>
            <span className="muted" style={{ fontSize: 13 }}>
              They appoint their own faculty from there.
            </span>
          </div>
        </form>
      </section>

      <section>
        <h3>INSTITUTION</h3>
        <form onSubmit={saveDetails}>
          <div className="grid-2">
            <label>
              <span>Name</span>
              <input
                value={edit.name}
                onChange={(e) => setEdit({ ...edit, name: e.target.value })}
                required
              />
            </label>
            <label>
              <span>Restrict sign-in to email domain</span>
              <input
                value={edit.allowedEmailDomain}
                onChange={(e) => setEdit({ ...edit, allowedEmailDomain: e.target.value })}
                placeholder="any"
              />
            </label>
          </div>
          <div className="row">
            <button className="primary" disabled={busy}>
              {busy ? "Saving…" : "Save changes"}
            </button>
            <button type="button" className={u.active ? "danger" : ""} onClick={toggleActive}>
              {u.active ? "Suspend institution" : "Reactivate institution"}
            </button>
            <Link to={`/audit?universityId=${u.id}`}><button type="button">Audit log</button></Link>
          </div>
          <p className="muted" style={{ fontSize: 13, marginBottom: 0 }}>
            The code <strong>{u.code}</strong> is permanent: courses and audit records are filed
            under it. Changing the domain only affects who may sign in from now on.
          </p>
        </form>
      </section>
    </div>
  );
}
